import { hashKey } from '@unifyapps/network/react-query';

type WithFallback<T> = T extends string ? string : string | null;

/*
 * WARNING: This function is exposed in the no-code platform utilities.
 * Any API changes must maintain backward compatibility.
 * Documentation updates must be made in packages/carbon/docs/website/utilities.md
 * before deploying breaking changes.
 */
export function safeJsonStringify<T extends string | undefined>(
  obj: unknown,
  fallback?: T,
  space = 0,
  replacer?: (key: string, value: unknown) => unknown,
): WithFallback<T> {
  try {
    return JSON.stringify(obj, replacer, space);
  } catch (e) {
    // console.error('Error converting to JSON', e, obj);
    return fallback !== undefined ? fallback : (null as WithFallback<T>);
  }
}

/*
 * Recursively serializes `value` with object keys sorted at every level so the
 * output is invariant to property insertion order. Use this when the string is
 * compared for equality (cache keys, React keys, memo deps) — `safeJsonStringify`
 * is order-sensitive and produces different strings for `{a:1,b:2}` vs `{b:2,a:1}`.
 *
 * Delegates the key-sorting to react-query's `hashKey` (the same order-stable
 * JSON.stringify replacer it uses to hash query keys). `hashKey` throws on
 * circular refs; we swallow and return '' because this feeds a render-path React
 * key and must never crash the render.
 */
export function stableJsonStringify(value: unknown): string {
  try {
    // hashKey types its arg as a QueryKey (array) but serializes any value at
    // runtime, sorting a top-level object via the same root replacer — so the
    // output matches the array-free form and the cast is safe.
    return hashKey(value as Parameters<typeof hashKey>[0]);
  } catch {
    return '';
  }
}

/*
 * WARNING: This function is exposed in the no-code platform utilities.
 * Any API changes must maintain backward compatibility.
 * Documentation updates must be made in packages/carbon/docs/website/utilities.md
 * before deploying breaking changes.
 */
export function safeJsonParse<T = Record<string, unknown>>(
  str: string,
  fallback: T = {} as T,
  retriever?: (key: string, value: unknown) => unknown,
): T {
  try {
    const parsed = JSON.parse(str, retriever);
    return parsed ?? fallback;
  } catch (e) {
    // console.error('Error parsing JSON', e, str);
    return fallback;
  }
}
